/**
 * Creates CSV file with PCA result outcomes and download it as a file.
 *
 * @param {Object} res - object with PCA results.
 * @param {Object} dataset - object with dataset used to get the results.
 * @param {string} name - name of the results (e.g. 'cal')
 * @param {Array} outliers - array with row indices of excluded data rows.
 * @param {Object} model - object with PCA model.
 */
export function downloadResults(res, dataset, name, outliers, model) {

   if (!outliers || !Array.isArray(outliers)) {
      outliers = [];
   }

   // number of rows with/without outliers
   const nAll = dataset.values.nrows;
   const nCols = dataset.values.ncols;
   const nOut = outliers.length;
   const n = nAll - nOut;
   const nComp = res.ncomp;
   const nStat = 4; // var, expvar, resvar, respexpvar

   // number of empty columns to add in addition to row name and first value
   const nfiller = Math.max(nComp, nStat);
   const filler = Array(nfiller).fill('');
   const fillerComp = nComp > nfiller ? Array(nfiller - nComp).fill('') : [];

   // vector with component names/labels and values separator
   const compNames = Array(nComp).fill(0).map((v, i) => 'PC' + (i + 1));
   const sep = dataset.sep

   // function for adding component based outcomes for every row
   function addChunk(name, offset, rowlabels) {
      out += '\n';
      out += [name + ': ', '', ...filler].join(sep) + '\n';
      out += ['object', 'group', 'outlier', ...compNames, ...fillerComp].join(sep) + '\n';
      const values = res.v.subarray(offset, offset + nComp * n);
      let j = 0;
      for (let i = 0; i < nAll; i++) {
         const groupname = dataset.classes ? dataset.classes[i] : '-';

         // outlier
         if (nOut > 0 && outliers.includes(i)) {
            out += [rowlabels[i], groupname, 'yes', ...Array(nComp).fill('-'), ...fillerComp].join(sep) + '\n';
            continue;
         }

         // regular
         const valuesi = Array(nComp);
         for (let a = 0; a < nComp; a++) {
            valuesi[a] = values[a * nComp + i];
         }
         out += [rowlabels[i], groupname, '', ...valuesi, fillerComp].join(sep) + '\n';
         j = j + 1;
      }
      out += '\n';
   }


   // general information
   let out = '';
   out += ['Number of components:', nComp, ...filler].join(sep) + '\n';
   out += ['Center: ', res.center, ...filler].join(sep) + '\n';
   out += ['Scale: ', res.scale, ...filler].join(sep) + '\n';

   // statistics
   out += '\n';
   out += ['Statistics: ', '', ...filler].join(sep) + '\n';
   if (model) {
      // if model is provided we also show eigenvalues
      // but for the sake of simplicity we get the from "res" object
      const fillerStat = nStat > nfiller ? Array(nfiller - nStat - 1).fill('') : [];
      out += ['', 'PC', 'Expvar', 'Cumexpvar', 'Resvar', 'Cumresvar', 'Eigenvalues', ...fillerStat].join(dataset.sep) + '\n';
      for (let a = 0; a < nComp; a++) {
         out += [
            '',
            a + 1,
            res.stat.expvar[a],
            res.stat.cumexpvar[a],
            res.stat.resvar[a],
            res.stat.cumresvar[a],
            res.eigenvals[a],
            ...fillerStat
         ].join(dataset.sep) + '\n';
      }
   } else {
      const fillerStat = nStat > nfiller ? Array(nfiller - nStat).fill('') : [];
      out += ['', 'PC', 'Expvar', 'Cumexpvar', 'Resvar', 'Cumresvar', ...fillerStat].join(sep) + '\n';
      for (let a = 0; a < nComp; a++) {
         out += [
            '',
            a + 1,
            res.stat.expvar[a],
            res.stat.cumexpvar[a],
            res.stat.resvar[a],
            res.stat.cumresvar[a],
            ...fillerStat
         ].join(sep) + '\n';
      }
   }
   out += '\n';

   // if model is provided show loadings as well
   if (model) {
      out += '\n';
      out += ['Loading, center and scale values: ', '', ...filler].join(sep) + '\n';
      out += ['variable', 'center', 'scale', ...compNames, ...fillerComp].join(sep) + '\n';
      const center = model.v.subarray(2, 2 + nCols);
      const scale = model.v.subarray(2 + nCols, 2 + nCols * 2);
      const loadings = model.v.subarray(2 + nCols * 2, n + nCols * 2 + nComp * nCols);
      let j = 0;
      for (let i = 0; i < nCols; i++) {
         const loadingsi = Array(nComp);
         for (let a = 0; a < nComp; a++) {
            loadingsi[a] = loadings[a * nComp + i];
         }
         out += [dataset.varlabels[i], center[i], scale[i], ...loadingsi, fillerComp].join(sep) + '\n';
         j = j + 1;
      }
      out += '\n';
   }

   // scores and distances
   addChunk('Scores', 0, dataset.objlabels);
   addChunk('Score distances, h', nComp * n, dataset.objlabels);
   addChunk('Orthogonal distances, q', nComp * n * 2, dataset.objlabels);


   // if decimal separator is not ".", replace all "." with the corect separator
   if (dataset.dec !== '.') {
      out = out.replace(/\./g, dataset.dec);
   }

   // add header
   out = ['Data filename:', dataset.fileName, ...filler].join(dataset.sep) + '\n' + out;
   let hdr = ['PCA results (' + name + ')', 'https://mda.tools/pca/', ...filler].join(dataset.sep) + '\n\n';

   // create Blob and start download process
   const blob = new Blob([hdr + out], { type: 'text/csv' });
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.setAttribute('href', url)
   a.setAttribute('download', `pca-results-${name}.csv`);
   a.click()
}


/**
 * Creates JSON with model object, stringify it and download as a file.
 *
 * @param {Object} model - object with PCA model
 */
export function downloadModel(model) {

   const blob = new Blob([JSON.stringify(
      {
         center: model.center,
         class: model.class,
         hash: model.hash,
         exclvars: model.exclvars,
         ncols: model.ncols,
         ncomp: model.ncomp,
         nrows: model.nrows,
         prep: model.prep,
         scale: model.scale,
         v: Array.from(model.v),
         exclvars: model.exclvars,
         varlabels: model.varlabels,
         varvalues: model.varvalues,
         varvaluesName: model.varvaluesName,
         varvaluesUnits: model.varvaluesUnits,
      }
   )], { type: 'application/json' });
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.setAttribute('href', url)
   a.setAttribute('download', 'pca-model.json');
   a.click()
}
